import { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import ServiceBoxes from "@/components/ServiceBoxes";
import { HeroImage } from "@/components/HeroImage";
import { usePageSEO } from "@/hooks/usePageTitle";
import { ArrowDown } from "lucide-react";
import { ImagePreloader } from "@/components/ImagePreloader";
import IndexSkeleton from "@/components/skeletons/IndexSkeleton";
import NewsletterSignupSection from "@/components/NewsletterSignupSection";
import { NewsletterSignupModal } from "@/components/NewsletterSignupModal";
import GoogleReviewsSingle from "@/components/GoogleReviewsSingle";
import YouTubeButton from "@/components/YouTubeButton";
import { useAdminShows } from "@/hooks/useAdminShows";

export default function Index() {
  const [isPageReady, setIsPageReady] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isNewsletterModalOpen, setIsNewsletterModalOpen] = useState(false);
  const { data: adminShows } = useAdminShows();

  const hasFreeShow = useMemo(() => {
    if (!adminShows) return false;
    const now = new Date();
    return adminShows.some((show) => {
      if (!show.is_free) return false;
      const showDateTime = new Date(`${show.show_date}T${show.show_time || "00:00"}`);
      return showDateTime.getTime() + 5 * 60 * 1000 >= now.getTime();
    });
  }, [adminShows]);

  usePageSEO({
    title: "Lilla improteatern – Improvisationsteater i Stockholm | Kurser & föreställningar",
    description:
      "Improvisationsteater i Stockholm. Lilla improteatern erbjuder improvisationskurser, improv comedy och föreställningar – för nybörjare till erfarna improvisatörer.",
    canonicalPath: "/",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Lilla improteatern",
      url: "https://improteatern.se",
      description: "Improvisationsteater i Stockholm – kurser, improv comedy och föreställningar.",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Stockholm",
        addressCountry: "SE",
      },
      sameAs: ["https://www.instagram.com/lillaimproteatern", "https://www.facebook.com/lillaimproteatern"],
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Sätt sidan som redo när data är laddad
  useEffect(() => {
    if (dataLoaded) {
      // Kort delay för smooth transition
      setTimeout(() => setIsPageReady(true), 150);
    }
  }, [dataLoaded]);

  // Visa skeleton medan vi laddar
  if (!isPageReady) {
    return (
      <>
        <ImagePreloader />
        <Header />
        <IndexSkeleton onDataReady={() => setDataLoaded(true)} />
      </>
    );
  }

  return (
    <div className="relative min-h-screen animate-fade-in overflow-x-hidden">
      <ImagePreloader />
      <Header />

      {/* Hero image with text overlay */}
      <div className="relative">
        <HeroImage pageKey="index" priority={true}>
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-black/50" />

          {/* Text overlay - positioned in lower area */}
          <div className="absolute bottom-[5%] left-1/2 transform -translate-x-1/2 px-4 w-full max-w-[90vw] sm:max-w-[500px]">
            <h3
              className="font-rajdhani text-[18px] sm:text-[20px] md:text-[22px] text-center mb-6"
              style={{ color: "rgb(var(--white))" }}
            >
              Vi är en plats för dig som vill uppleva
              <br />
              improv comedy i&nbsp;Stockholm.
            </h3>

            {/* Arrow visible on both mobile and desktop */}
            <div className="flex justify-center">
              <ArrowDown size={24} strokeWidth={2} className="animate-bounce" style={{ color: "rgb(var(--white))" }} />
            </div>
          </div>
        </HeroImage>
      </div>

      {/* White section with lazy-loaded ServiceBoxes */}
      <div className="bg-white py-10 md:py-12 rounded-t-lg md:rounded-t-none">
        <div className="container mx-auto px-4 max-w-6xl">
          <ServiceBoxes hasFreeShow={hasFreeShow} />
        </div>
      </div>

      {/* Google reviews carousel */}
      <div className="bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <GoogleReviewsSingle />
          <div className="flex justify-center pb-10 md:pb-12">
            <YouTubeButton />
          </div>
        </div>
      </div>

      {/* Newsletter signup section */}
      <div className="bg-[rgb(var(--newsletter-gray))] overflow-hidden">
        <div className="container mx-auto px-4 max-w-6xl">
          <NewsletterSignupSection onSignupClick={() => setIsNewsletterModalOpen(true)} />
        </div>
      </div>

      <NewsletterSignupModal 
        open={isNewsletterModalOpen} 
        onOpenChange={setIsNewsletterModalOpen} 
      />
    </div>
  );
}
